import React, { useState } from 'react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  Menu, Upload, Bell, Settings, LogOut, User, Building2, ChevronDown, Check,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

import { Badge } from './ui/badge';
import { UploadDialog } from './UploadDialog';
import { NotificationDialog } from './NotificationDialog';
import { SettingsDialog } from './SettingsDialog';
import { useMe, useActiveClient, usePermission } from '../hooks/useMe';
import { useAuthStore } from '../store/auth';
import { useNotifications } from '../hooks/api/notifications';

const sectionTitles = {
  dashboard: 'Dashboard',
  projects: '',
  reports: '',
  alerts: '',
  analytics: '',
};

export function Header({ activeSection, onToggleSidebar, onLogout }) {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  const me = useMe();
  const activeClient = useActiveClient();
  const canUpload = usePermission('file:upload');
  const setActiveClientId = useAuthStore((s) => s.setActiveClientId);

  // Live unread notification count for the bell badge.
  // `useNotifications` already 403s gracefully and returns no data when no
  // tenant is set; fall back to "0" when total isn't known yet.
  const { data: notifPage } = useNotifications({
    status: 'unread', page_size: 1,
  });
  const unreadCount = notifPage?.total ?? 0;

  const initials = (me?.full_name || me?.email || 'AK')
    .split(/[\s@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('');
  const roleLabel = me?.is_super_admin
    ? 'Super Admin'
    : (activeClient?.is_client_admin ? 'Client Admin' : 'Client User');

  // Tenant switcher only meaningful when the user belongs to multiple clients
  // OR is a super-admin (super-admins see every active client and can switch).
  const tenants = me?.clients || [];
  const showTenantSwitcher = me?.is_super_admin || tenants.length > 1;

  return (
    <>
      <header className="h-20 bg-card border-b border-border flex items-center justify-between px-6 shadow-sm">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleSidebar}
            className="md:hidden"
          >
            <Menu className="w-5 h-5" />
          </Button>

          {sectionTitles[activeSection] && (
            <div>
              <h1 className="text-xl font-semibold text-foreground">
                {sectionTitles[activeSection]}
              </h1>
              <p className="text-sm text-muted-foreground">
                Welcome back to your analytics platform
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-3">
          {/* Tenant switcher — super-admins (and multi-tenant client users)
              pick which client's data to view. Sets activeClientId in the
              auth store; the axios interceptor sends X-Client-Id on every
              subsequent request. */}
          {showTenantSwitcher && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Building2 className="w-4 h-4" />
                  <span className="max-w-[140px] truncate">
                    {activeClient?.name || 'Select tenant'}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>
                  {me?.is_super_admin ? 'All clients' : 'Switch tenant'}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {tenants.length === 0 && (
                  <DropdownMenuItem disabled>
                    No clients available
                  </DropdownMenuItem>
                )}
                {tenants.map((c) => {
                  const active = activeClient?.id === c.id;
                  return (
                    <DropdownMenuItem
                      key={c.id}
                      className="cursor-pointer"
                      onClick={() => setActiveClientId(c.id)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{c.name}</div>
                        <div className="text-[11px] text-muted-foreground font-mono">{c.code}</div>
                      </div>
                      {active && <Check className="w-4 h-4 text-sky-400 ml-2 shrink-0" />}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Upload Files (gated on file:upload permission) */}
          {canUpload && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setUploadDialogOpen(true)}
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Files
            </Button>
          )}

          {/* Notifications — live unread count */}
          <Button
            variant="ghost"
            size="sm"
            className="relative"
            onClick={() => setNotificationDialogOpen(true)}
            aria-label="Notifications"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <Badge className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 flex items-center justify-center p-0 px-1 bg-destructive text-destructive-foreground text-xs">
                {unreadCount > 99 ? '99+' : unreadCount}
              </Badge>
            )}
          </Button>

          {/* Settings */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSettingsDialogOpen(true)}
          >
            <Settings className="w-5 h-5" />
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="flex items-center space-x-2 hover:bg-accent"
              >
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {initials || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden md:block text-left">
                  <div className="text-sm font-medium">
                    {me?.full_name || me?.email || 'Account'}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {roleLabel}{activeClient ? ` · ${activeClient.name}` : ''}
                  </div>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem>
                <User className="w-4 h-4 mr-2" />
                Profile Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSettingsDialogOpen(true)}>
                <Settings className="w-4 h-4 mr-2" />
                Preferences
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:bg-destructive/10 focus:text-destructive cursor-pointer"
                onClick={onLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Dialogs */}
      <UploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
      />
      <NotificationDialog
        open={notificationDialogOpen}
        onOpenChange={setNotificationDialogOpen}
      />
      <SettingsDialog
        open={settingsDialogOpen}
        onOpenChange={setSettingsDialogOpen}
      />
    </>
  );
}
